/*
 * File: OcdOwnerSelect.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 20 Jan 2022 4:37:42 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Select, Space, Spin } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { usePrevious } from 'hooks/usePrevious';
import { OCDModel } from 'models/OCDModel';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from 'redux/rootReducer';
import { useGetOwnersQuery } from 'redux/services/useAnimalService';
import { useDebouncedCallback } from 'use-debounce';
interface Props {
  onChange: (value: string) => void;
}

const OcdOwnerSelect = ({ onChange }: Props): JSX.Element => {
  const { selectedCompanyDataAreaId: dataAreaId } = useSelector((state: ReduxState) => state.salesOrders);
  const [offset, _setOffset] = useState(0);
  const [searchValue, _setSearchValue] = useState('');
  const [owners, _setOwners] = useState<OCDModel[]>([]);

  const prevSearchValue = usePrevious(searchValue);

  /* ******************** Hooks ******************** */
  const { data, isFetching } = useGetOwnersQuery({ overrideSkipTake: true, offset, ownerNameContains: searchValue });

  /* ******************** Variables ******************** */
  const options = useMemo(() => owners.map((option) => ({ label: option.name, value: option.name })), [owners]);

  /* ******************** Function ******************** */
  const debounceSearch = useDebouncedCallback((value: string) => {
    _setOffset(0);
    _setOwners([]);
    _setSearchValue(value);
  }, 500);

  const handleScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const bottom = e.currentTarget.scrollTop + e.currentTarget.offsetHeight === e.currentTarget.scrollHeight;

    if (bottom && data?.totalCount && offset + 25 < data.totalCount && !isFetching) _setOffset((prevState) => prevState + 25);
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    if (isFetching && !offset)
      return (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />
        </div>
      );

    const moreResultsJSX =
      isFetching && offset ? (
        <div style={{ padding: '0px 10px' }}>
          <Space>
            <Spin size="small" indicator={<BlockLoader direction="loader loader--slideUp" />} />
          </Space>
        </div>
      ) : null;

    return (
      <div>
        {menu}
        {moreResultsJSX}
      </div>
    );
  };

  /* ******************** Use Effects ******************** */
  useEffect(() => {
    if (data !== undefined && !isFetching) {
      if (prevSearchValue === searchValue && offset !== 0) _setOwners((prevState) => [...prevState, ...data.data]);
      if (prevSearchValue !== searchValue || offset === 0) _setOwners(data.data);
    }
  }, [data, isFetching, offset, prevSearchValue, searchValue]);

  return (
    <Select
      dropdownRender={dropDownRender}
      filterOption={false}
      loading={isFetching}
      onPopupScroll={handleScroll}
      onSearch={debounceSearch}
      onSelect={onChange}
      options={options}
      placeholder="Owner"
      showSearch
    />
  );
};

export default OcdOwnerSelect;
